import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable, Subject, catchError, forkJoin, map, of, takeUntil } from 'rxjs';
import { MarketingCampaignApiService } from '../services/marketing-campaign-api.service';
import { CampaignStatus, MarketingCampaign,
  campaignStatusLabel,
} from '../models/marketing-campaign.model';
import { EmailChannelApiService } from '../services/email-channel-api.service';
import { EmailChannelView } from '../models/email-channel.model';
import { BulkChannelModalComponent } from '../bulk-channel-modal/bulk-channel-modal.component';
import { CampaignDetailModalComponent } from '../campaign-detail-modal/campaign-detail-modal.component';
import { CampaignTab } from '../campaign-detail-tabs/campaign-detail-tabs.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { MarketingFolderApiService, CampaignFolder } from '../services/marketing-folder-api.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { DisableWithoutPermissionDirective } from '../../../shared/directives/disable-without-permission.directive';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';

type StatusFilter = 'ALL' | CampaignStatus;

/** Resultado de mover UN correo de carpeta (usado por el bulk con forkJoin). */
interface MoveResult {
  updated: MarketingCampaign | null;
  error: string | null;
}

@Component({
  selector: 'app-campaign-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    BulkChannelModalComponent,
    CampaignDetailModalComponent,
    HasPermissionDirective,
    DisableWithoutPermissionDirective,
    ModalComponent,
  ],
  templateUrl: './campaign-list.component.html',
})
export class CampaignListComponent implements OnInit, OnDestroy {
  private readonly api = inject(MarketingCampaignApiService);
  private readonly channelApi = inject(EmailChannelApiService);
  private readonly folderApi = inject(MarketingFolderApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly toast = inject(ToastService);

  private readonly destroy$ = new Subject<void>();

  loading = true;
  error: string | null = null;
  campaigns: MarketingCampaign[] = [];

  searchTerm = '';
  activeFilter: StatusFilter = 'ALL';
  selectedCampaigns = new Set<number>();

  // Bulk channel state
  bulkChannel: EmailChannelView | null = null;
  bulkModalOpen = false;
  channels: EmailChannelView[] = [];

  // Folder state
  folders: CampaignFolder[] = [];
  folderPanelOpen = false;
  activeFolderId: number | null = null;
  folderEditingId: number | null = null;
  folderEditName = '';
  newFolderName = '';
  creatingFolder = false;
  moveToFolderMenuId: number | null = null;
  /** Modal "Mover a carpeta" de la selección múltiple. 'NONE' = sacar de la carpeta; null = sin elegir. */
  bulkMoveModalOpen = false;
  bulkMoveTarget: number | 'NONE' | null = null;

  // Detalle en modal (Inc 29) — el estándar del CRM (contactos / leads / deals / tickets).
  showDetailModal = false;
  detailCampaignId: number | null = null;
  detailInitialTab: CampaignTab = 'overview';
  /** true cuando el modal se abrió por deep-link `/marketing-email/:id` (y no desde la lista). */
  private deepLinkedDetail = false;

  readonly statusOrder: CampaignStatus[] = ['DRAFT', 'SCHEDULED', 'SENDING', 'SENT', 'CANCELLED', 'FAILED'];

  ngOnInit(): void {
    this.loadAll();
    this.loadFolders();

    // Inc 29: deep-link `/#/marketing-email/:id` → esta misma lista, con el modal
    // de detalle abierto encima. La ruta se conserva para no romper enlaces
    // existentes (notificaciones, correos, el form tras "Lanzar correo").
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const raw = params.get('id');
      const id = raw != null ? Number(raw) : NaN;
      if (Number.isNaN(id) || id <= 0) return;
      const rawTab = this.route.snapshot.queryParamMap.get('tab');
      this.deepLinkedDetail = true;
      this.detailInitialTab = this.resolveTab(rawTab);
      this.detailCampaignId = id;
      this.showDetailModal = true;
      // `?tab=shipping` ya no existe: normalizamos la URL a la pestaña resuelta.
      if (rawTab === 'shipping') this.onDetailTabChange(this.detailInitialTab);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Traduce el query param `?tab=` a una pestaña válida.
   * Inc 36: `shipping` ya no existe (la tabla de destinatarios vive dentro de
   * "Métricas") → se redirige a `metrics` para no romper enlaces antiguos.
   */
  private resolveTab(raw: string | null): CampaignTab {
    if (raw === 'shipping') return 'metrics';
    if (raw === 'metrics' || raw === 'audience' || raw === 'content' || raw === 'overview') return raw;
    return 'overview';
  }

  loadAll(): void {
    this.loading = true;
    this.error = null;
    this.api.list().subscribe({
      next: (campaigns) => {
        this.campaigns = (campaigns || []).sort((a, b) => b.id - a.id);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.message || 'No se pudieron cargar los correos.';
      },
    });
    this.loadChannels();
  }

  private loadChannels(): void {
    this.channelApi.listMine().pipe(catchError(() => of([] as EmailChannelView[])))
      .subscribe(channels => {
        this.channels = channels || [];
        // Heurística: primer canal isPrimary o el primero conectado como sugerencia.
        // El backend no expone GET del bulk_channel; lo administramos por UX local.
        const stored = localStorage.getItem('mkt_bulk_channel');
        if (stored) {
          try {
            const { channelType, channelRefId } = JSON.parse(stored);
            this.bulkChannel = this.channels.find(c =>
              c.channelType === channelType && c.channelRefId === channelRefId) || null;
          } catch { /* ignore */ }
        }
      });
  }

  /** true cuando el usuario tiene al menos un canal de envío conectado. */
  get hasConnectedChannels(): boolean {
    return this.channels.some(c => c.isConnected);
  }

  countByStatus(status: CampaignStatus): number {
    return this.campaigns.filter(c => c.status === status).length;
  }

  get filteredCampaigns(): MarketingCampaign[] {
    let list = this.campaigns;
    if (this.activeFolderId !== null) {
      list = list.filter(c => c.folderId === this.activeFolderId);
    }
    if (this.activeFilter !== 'ALL') {
      list = list.filter(c => c.status === this.activeFilter);
    }
    if (this.searchTerm) {
      const t = this.searchTerm.toLowerCase();
      list = list.filter(c =>
        c.name.toLowerCase().includes(t)
        || (c.subject || '').toLowerCase().includes(t));
    }
    return list;
  }

  setFilter(f: StatusFilter): void {
    this.activeFilter = f;
    this.activeFolderId = null;
    this.selectedCampaigns.clear();
  }

  isAllSelected(): boolean {
    const f = this.filteredCampaigns;
    return f.length > 0 && f.every(c => this.selectedCampaigns.has(c.id));
  }

  toggleSelectAll(event: Event): void {
    if ((event.target as HTMLInputElement).checked) {
      this.filteredCampaigns.forEach(c => this.selectedCampaigns.add(c.id));
    } else {
      this.filteredCampaigns.forEach(c => this.selectedCampaigns.delete(c.id));
    }
  }

  toggleSelect(id: number, event: Event): void {
    event.stopPropagation();
    if (this.selectedCampaigns.has(id)) {
      this.selectedCampaigns.delete(id);
    } else {
      this.selectedCampaigns.add(id);
    }
  }

  // ===== Acciones =====
  newCampaign(): void {
    this.router.navigate(['/marketing-email/crear']);
  }

  edit(c: MarketingCampaign): void {
    if (c.status === 'SENDING' || c.status === 'SENT') {
      this.openDetail(c);
      return;
    }
    this.router.navigate(['/marketing-email/editar', c.id]);
  }

  /** Inc 29: el detalle es un MODAL sobre la lista, no una navegación. */
  openDetail(c: MarketingCampaign): void {
    this.detailInitialTab = 'overview';
    this.detailCampaignId = c.id;
    this.showDetailModal = true;
  }

  closeDetail(): void {
    this.showDetailModal = false;
    this.detailCampaignId = null;
    this.detailInitialTab = 'overview';
    // Si llegamos por deep-link `/marketing-email/:id`, volver a la lista para
    // que la URL refleje lo que se ve y el mismo correo pueda reabrirse luego.
    if (this.deepLinkedDetail) {
      this.deepLinkedDetail = false;
      this.router.navigate(['/marketing-email/estado'], { replaceUrl: true });
    }
  }

  /** El modal cambió el correo (cancelar / destrabar / reenviar / eliminar). */
  onDetailChanged(): void {
    this.loadAll();
    this.loadFolders();
  }

  /** Mantiene `?tab=` sincronizado mientras el detalle está en modo deep-link. */
  onDetailTabChange(tab: CampaignTab): void {
    this.detailInitialTab = tab;
    if (!this.deepLinkedDetail) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab === 'overview' ? null : tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  async cancel(c: MarketingCampaign): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Cancelar correo',
      message: `¿Cancelar el correo "${c.name}"? No se podrá enviar después.`,
      confirmText: 'Cancelar correo'
    });
    if (!ok) return;
    this.api.cancel(c.id).subscribe({
      next: (updated) => {
        this.campaigns = this.campaigns.map(x => x.id === c.id ? updated : x);
      },
      error: (err) => {
        this.error = err?.error?.message || 'No se pudo cancelar.';
      },
    });
  }

  async remove(c: MarketingCampaign): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar correo',
      message: `¿Eliminar el correo "${c.name}"? Esta acción no se puede deshacer.`
    });
    if (!ok) return;
    this.api.delete(c.id).subscribe({
      next: () => {
        this.campaigns = this.campaigns.filter(x => x.id !== c.id);
        this.toast.deleted('Correo');
      },
      error: (err) => {
        this.error = err?.error?.message || 'No se pudo eliminar.';
        this.toast.error({ title: 'Error', message: err?.error?.message || 'No se pudo eliminar.' });
      },
    });
  }

  duplicate(c: MarketingCampaign): void {
    // Cliente-side: crea una nueva en DRAFT con los mismos datos.
    this.api.create({
      name: c.name + ' (copia)',
      subject: c.subject,
      preheader: c.preheader,
      bodyHtml: c.bodyHtml,
      bodyText: c.bodyText,
      templateCode: c.templateCode,
      channelType: c.channelType || null,
      channelRefId: c.channelRefId || null,
      scheduledAt: null,
    }).subscribe({
      next: (newCampaign) => {
        this.campaigns = [newCampaign, ...this.campaigns];
        this.router.navigate(['/marketing-email/editar', newCampaign.id]);
      },
      error: (err) => {
        this.error = err?.error?.message || 'No se pudo duplicar el correo.';
      },
    });
  }

  // ===== Bulk channel =====
  openBulkModal(): void { this.bulkModalOpen = true; }
  closeBulkModal(): void { this.bulkModalOpen = false; }

  // ===== Folders =====

  loadFolders(): void {
    this.folderApi.list().pipe(catchError(() => of([] as CampaignFolder[])))
      .subscribe(folders => { this.folders = folders; });
  }

  toggleFolderPanel(): void {
    this.folderPanelOpen = !this.folderPanelOpen;
    if (!this.folderPanelOpen) {
      this.newFolderName = '';
      this.folderEditingId = null;
    }
  }

  setFolderFilter(folderId: number | null): void {
    this.activeFolderId = folderId;
    this.activeFilter = 'ALL';
    this.selectedCampaigns.clear();
  }

  createFolder(): void {
    const name = this.newFolderName.trim();
    if (!name) return;
    this.creatingFolder = true;
    this.folderApi.create(name).subscribe({
      next: (folder) => {
        this.folders = [...this.folders, folder].sort((a, b) => a.name.localeCompare(b.name));
        this.newFolderName = '';
        this.creatingFolder = false;
      },
      error: () => { this.creatingFolder = false; },
    });
  }

  startEditFolder(folder: CampaignFolder): void {
    this.folderEditingId = folder.id;
    this.folderEditName = folder.name;
  }

  saveEditFolder(folder: CampaignFolder): void {
    const name = this.folderEditName.trim();
    if (!name || name === folder.name) { this.folderEditingId = null; return; }
    this.folderApi.rename(folder.id, name).subscribe({
      next: (updated) => {
        this.folders = this.folders.map(f => f.id === updated.id ? updated : f)
          .sort((a, b) => a.name.localeCompare(b.name));
        this.folderEditingId = null;
      },
      error: () => { this.folderEditingId = null; },
    });
  }

  async deleteFolder(folder: CampaignFolder): Promise<void> {
    const msg = folder.campaignCount > 0
      ? `¿Eliminar la carpeta "${folder.name}"? Los ${folder.campaignCount} correo(s) quedarán sin carpeta.`
      : `¿Eliminar la carpeta "${folder.name}"?`;
    const ok = await this.confirmDialog.confirmDanger({ title: 'Eliminar carpeta', message: msg });
    if (!ok) return;
    this.folderApi.delete(folder.id).subscribe({
      next: () => {
        this.folders = this.folders.filter(f => f.id !== folder.id);
        this.campaigns = this.campaigns.map(c => c.folderId === folder.id ? { ...c, folderId: null } : c);
        if (this.activeFolderId === folder.id) this.activeFolderId = null;
      },
      error: () => this.toast.error({ title: 'Error', message: 'No se pudo eliminar la carpeta.' }),
    });
  }

  /** Nombre de una carpeta por id (para los mensajes de los toasts). */
  private folderName(folderId: number): string {
    return this.folders.find(f => f.id === folderId)?.name ?? 'la carpeta';
  }

  /**
   * Inc 40: mueve un correo a una carpeta en CUALQUIER estado.
   * Antes se hacía con `update()` (PUT con el objeto completo), que valida
   * editabilidad → 400 en SENDING/SENT ("solo se podían mover los Borradores")
   * y además omitía `attachments`, arriesgando borrar los adjuntos.
   * Ahora usa el endpoint dedicado PATCH /{id}/folder.
   * Inc 41: confirma con toast (mensaje distinto para mover vs sacar).
   */
  moveCampaignToFolder(campaign: MarketingCampaign, folderId: number | null): void {
    this.moveToFolderMenuId = null;
    this.api.moveToFolder(campaign.id, folderId).subscribe({
      next: (updated) => {
        this.campaigns = this.campaigns.map(c => c.id === updated.id ? updated : c);
        this.loadFolders();
        this.toast.success({
          title: 'Correo movido',
          message: folderId !== null
            ? `Correo movido a "${this.folderName(folderId)}".`
            : 'Correo quitado de la carpeta.',
        });
      },
      error: (err) => this.toast.error({
        title: 'Error',
        message: err?.error?.message || 'No se pudo mover el correo.',
      }),
    });
  }

  /**
   * Inc 40/41: mueve la selección con el endpoint dedicado y UN SOLO toast
   * agregado al terminar (antes: N PUTs sueltos y ningún feedback).
   */
  openBulkMoveModal(): void {
    if (this.selectedCampaigns.size === 0) return;
    this.moveToFolderMenuId = null;
    this.bulkMoveTarget = null;
    this.bulkMoveModalOpen = true;
    // Refresca la lista por si se crearon carpetas en otra pestaña.
    this.loadFolders();
  }

  closeBulkMoveModal(): void {
    this.bulkMoveModalOpen = false;
    this.bulkMoveTarget = null;
  }

  confirmBulkMove(): void {
    if (this.bulkMoveTarget === null) return;
    const folderId = this.bulkMoveTarget === 'NONE' ? null : this.bulkMoveTarget;
    this.closeBulkMoveModal();
    this.moveBulkToFolder(folderId);
  }

  moveBulkToFolder(folderId: number | null): void {
    this.moveToFolderMenuId = null;
    const ids = Array.from(this.selectedCampaigns);
    if (ids.length === 0) return;

    const requests: Observable<MoveResult>[] = ids.map(id => this.api.moveToFolder(id, folderId).pipe(
      map((updated): MoveResult => ({ updated, error: null })),
      catchError(err => of<MoveResult>({
        updated: null,
        error: err?.error?.message || 'No se pudo mover el correo.',
      })),
    ));

    forkJoin(requests).pipe(takeUntil(this.destroy$)).subscribe(results => {
      let moved = 0;
      let firstError: string | null = null;
      for (const r of results) {
        const updated = r.updated;
        if (updated) {
          this.campaigns = this.campaigns.map(c => c.id === updated.id ? updated : c);
          moved++;
        } else if (!firstError) {
          firstError = r.error;
        }
      }
      const failed = results.length - moved;
      this.selectedCampaigns.clear();
      this.loadFolders();

      if (moved > 0) {
        const plural = moved === 1 ? '' : 's';
        this.toast.success({
          title: 'Correos movidos',
          message: folderId !== null
            ? `${moved} correo${plural} movido${plural} a "${this.folderName(folderId)}".`
            : `${moved} correo${plural} quitado${plural} de la carpeta.`,
        });
      }
      if (failed > 0) {
        this.toast.error({
          title: 'Error',
          message: `No se pudo mover ${failed} correo(s). ${firstError ?? ''}`.trim(),
        });
      }
    });
  }

  onBulkSaved(payload: { channelType: string; channelRefId: number } | null): void {
    if (payload) {
      localStorage.setItem('mkt_bulk_channel', JSON.stringify(payload));
      this.bulkChannel = this.channels.find(c =>
        c.channelType === payload.channelType && c.channelRefId === payload.channelRefId) || null;
    } else {
      localStorage.removeItem('mkt_bulk_channel');
      this.bulkChannel = null;
    }
    this.bulkModalOpen = false;
  }

  // ===== Helpers =====
  formatDate(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  channelLabel(type: string | null | undefined): string {
    if (!type) return 'canal predeterminado';
    const map: Record<string, string> = {
      USER_GMAIL: 'Gmail', USER_MICROSOFT: 'Outlook',
      USER_SMTP: 'SMTP', USER_API: 'API', SYSTEM_API: 'sistema',
    };
    return map[type] || type;
  }

  statusBadgeClass(status: CampaignStatus): string {
    const map: Record<CampaignStatus, string> = {
      DRAFT: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      SCHEDULED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      SENDING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      SENT: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
      CANCELLED: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
      FAILED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    };
    return map[status] || map.DRAFT;
  }

  statusDotClass(status: CampaignStatus): string {
    const map: Record<CampaignStatus, string> = {
      DRAFT: 'bg-gray-400',
      SCHEDULED: 'bg-amber-500',
      SENDING: 'bg-amber-600',
      SENT: 'bg-teal-500',
      CANCELLED: 'border-2 border-gray-400',
      FAILED: 'bg-red-500',
    };
    return map[status] ?? 'bg-gray-400';
  }

  formatUpdatedAt(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('es', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  statusLabel(status: CampaignStatus): string {
    return campaignStatusLabel(status);
  }

  // ===== Estado derivado (Inc 5) =====
  // Para correos ya enviados, el badge no se deriva solo del `status`: uno
  // SENT con deliveredCount=0 y failedCount>0 NO es "Entregado".
  private isDeliveryFailed(c: MarketingCampaign): boolean {
    if (c.status !== 'SENT') return false;
    const delivered = c.deliveredCount ?? 0;
    const failed = c.failedCount ?? 0;
    const bounced = c.bouncedCount ?? 0;
    return delivered === 0 && (failed > 0 || bounced > 0);
  }

  private isDelivered(c: MarketingCampaign): boolean {
    return c.status === 'SENT' && (c.deliveredCount ?? 0) > 0;
  }

  /** Etiqueta del badge derivada de la entrega real, no solo del `status`. */
  deliveryLabel(c: MarketingCampaign): string {
    if (this.isDelivered(c)) return 'Entregado';
    if (this.isDeliveryFailed(c)) return 'No entregado';
    return this.statusLabel(c.status);
  }

  deliveryBadgeClass(c: MarketingCampaign): string {
    if (this.isDelivered(c)) return this.statusBadgeClass('SENT');
    if (this.isDeliveryFailed(c)) return this.statusBadgeClass('FAILED');
    return this.statusBadgeClass(c.status);
  }

  deliveryDotClass(c: MarketingCampaign): string {
    if (this.isDelivered(c)) return this.statusDotClass('SENT');
    if (this.isDeliveryFailed(c)) return this.statusDotClass('FAILED');
    return this.statusDotClass(c.status);
  }

  /** Cantidad de destinatarios reales (poblada también en Borrador/Programado). Inc 13. */
  recipients(c: MarketingCampaign): number {
    return c.recipientCount ?? c.totalRecipients ?? 0;
  }

  /** Tasa de apertura del listado: aperturas únicas / entregados. '—' si no hay entregados. */
  openRate(c: MarketingCampaign): string {
    const delivered = c.deliveredCount ?? 0;
    const opened = c.openedCount ?? 0;
    if (delivered <= 0) return '—';
    return ((opened / delivered) * 100).toFixed(1) + '%';
  }

  // ===== Exportar correos (Inc 35) =====
  /** Exporta los correos actualmente visibles (respeta filtros/carpeta/búsqueda) a CSV legible. */
  exportCsv(): void {
    const rows = this.filteredCampaigns;
    if (rows.length === 0) {
      this.toast.info({ title: 'Nada que exportar', message: 'No hay correos que coincidan con los filtros actuales.' });
      return;
    }
    const headers = [
      'Nombre', 'Asunto', 'Estado', 'Destinatarios', 'Enviados', 'Entregados',
      'Fallidos', 'Rebotados', 'Aperturas', 'Tasa de apertura', 'Programado', 'Última actualización',
    ];
    const lines = rows.map(c => [
      c.name ?? '',
      c.subject ?? '',
      this.deliveryLabel(c),
      this.recipients(c),
      c.sentCount ?? 0,
      c.deliveredCount ?? 0,
      c.failedCount ?? 0,
      c.bouncedCount ?? 0,
      c.openedCount ?? 0,
      this.openRate(c),
      this.formatDate(c.scheduledAt),
      this.formatUpdatedAt(c.updatedAt),
    ].map(v => this.csvCell(v)).join(','));

    // BOM para que Excel interprete UTF-8 correctamente (acentos legibles).
    const csv = '﻿' + [headers.map(h => this.csvCell(h)).join(','), ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `correos-marketing_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    this.toast.success({ title: 'Exportación lista', message: `${rows.length} correo(s) exportado(s) a CSV.` });
  }

  private csvCell(value: unknown): string {
    const s = String(value ?? '');
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }

  canEdit(c: MarketingCampaign): boolean {
    return c.status === 'DRAFT' || c.status === 'SCHEDULED';
  }

  canCancel(c: MarketingCampaign): boolean {
    return c.status === 'DRAFT' || c.status === 'SCHEDULED';
  }

  canDelete(c: MarketingCampaign): boolean {
    return c.status !== 'SENDING';
  }
}
